import React, { useEffect, useState } from "react";
import {
  Box,
  Breadcrumbs,
  CircularProgress,
  Divider,
  Link,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
} from "@mui/material";
import FolderIcon from "@mui/icons-material/Folder";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { getFolder, getPlantFolder, getProjectFolder } from "./sharepointApi";

/**
 * Browses a plant's or project's real SharePoint folder via the backend's
 * /sharepoint proxy (see DCSTab/sharepoint.md) - no siteId/Graph path ever
 * touches the frontend, just plant/project names and folder paths built
 * from them. Pass `projectName` for a project (WBS) folder nested under
 * `plantName`; omit it for the plant's own top-level folder.
 *
 * Clicking a file calls `onOpenFile({ name, webUrl })` instead of opening it
 * itself - the caller (EmsExplorer) shows it inline in the right pane via
 * FilePreviewPanel rather than a new tab.
 */
export default function SharePointFolderBrowser({ plantName, projectName, onOpenFile }) {
  // Path segments navigated into below the root (plant or project) folder.
  const [path, setPath] = useState([]);
  const [folder, setFolder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setPath([]);
  }, [plantName, projectName]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);

    const load = async () => {
      try {
        const data =
          path.length === 0
            ? projectName
              ? await getProjectFolder(plantName, projectName)
              : await getPlantFolder(plantName)
            : await getFolder(
                [plantName, projectName, ...path].filter(Boolean).join("/"),
              );
        if (!cancelled) setFolder(data);
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();

    return () => {
      cancelled = true;
    };
  }, [plantName, projectName, path]);

  const rootLabel = projectName || plantName;

  return (
    <Paper variant="outlined" sx={{ overflow: "hidden" }}>
      <Box
        sx={{
          px: 2,
          py: 1,
          display: "flex",
          alignItems: "center",
          gap: 1,
          bgcolor: "action.hover",
        }}
      >
        <FolderIcon fontSize="small" color="primary" />
        <Breadcrumbs separator="/" sx={{ fontSize: "0.8125rem" }}>
          <Link
            component="button"
            underline={path.length ? "hover" : "none"}
            color={path.length ? "inherit" : "text.primary"}
            onClick={() => setPath([])}
            sx={{ fontSize: "0.8125rem" }}
          >
            {rootLabel}
          </Link>
          {path.map((segment, idx) => (
            <Link
              key={segment}
              component="button"
              underline={idx < path.length - 1 ? "hover" : "none"}
              color={idx < path.length - 1 ? "inherit" : "text.primary"}
              onClick={() => setPath(path.slice(0, idx + 1))}
              sx={{ fontSize: "0.8125rem" }}
            >
              {segment}
            </Link>
          ))}
        </Breadcrumbs>
      </Box>
      <Divider />

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={22} />
        </Box>
      ) : error ? (
        <Box sx={{ px: 2, py: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Couldn't load a SharePoint folder here — it may not exist yet.
          </Typography>
        </Box>
      ) : !folder?.children?.length ? (
        <Box sx={{ px: 2, py: 3 }}>
          <Typography variant="body2" color="text.secondary">
            This folder is empty.
          </Typography>
        </Box>
      ) : (
        <List disablePadding>
          {folder.children.map((item) => (
            <ListItemButton
              key={item.id}
              onClick={() => {
                if (item.isFolder) {
                  setPath([...path, item.name]);
                } else {
                  const itemWebUrl = `${folder.webUrl}/${encodeURIComponent(item.name)}`;
                  onOpenFile?.({ name: item.name, webUrl: itemWebUrl });
                }
              }}
              sx={{
                borderBottom: "1px solid",
                borderColor: "divider",
                "&:last-of-type": { borderBottom: "none" },
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                {item.isFolder ? (
                  <FolderIcon color="primary" fontSize="small" />
                ) : (
                  <InsertDriveFileIcon color="action" fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText
                primary={item.name}
                secondary={
                  item.lastModifiedDateTime
                    ? new Date(item.lastModifiedDateTime).toLocaleDateString()
                    : null
                }
                primaryTypographyProps={{ variant: "body2" }}
                secondaryTypographyProps={{ variant: "caption" }}
              />
            </ListItemButton>
          ))}
        </List>
      )}
    </Paper>
  );
}
